// Türen je Raum (seit 30.10.2026 ohne KI, Betreiber: „KI nur für Volumen und Möbel“): Unter den Wandfotos (aufklappbar) zeigt
// eine Skizze den Raum von oben, unten die Zimmertür, durch die der Kunde hereingekommen ist. Führt eine weitere Tür in
// einen anderen Raum (Balkon, Abstellraum, Verbindungstür), zieht der Kunde sie an ihre Wand – Finger halten, ziehen,
// loslassen (ZiehSkizze) – und tippt an, wohin sie führt: schon erfasster Raum oder neuer Raum. Ein neuer Raum hängt an
// diesem Raum (hallRoomId, doorWall) und kommt in der Fotoaufnahme direkt danach. Gespeichert in rooms.doors
// (x = Lage an der Wand, kein Foto).

import { useState } from "react";
import { ROOM_TYPE_LABEL, defaultPhotoMode, type RoomDoor, type RoomType, type RoomWall } from "@umzugsscan/shared";
import { useDraft, useStore } from "../hooks.ts";
import { RaumChips, tidyNames } from "../schritte/Grundriss.tsx";
import { newId } from "../store.ts";
import { roomName, type Room } from "../zustand.ts";
import { Button } from "./elemente.tsx";
import { ZiehSkizze, type SkizzenTuer, type Wand } from "./ZiehSkizze.tsx";

/** Neue Räume hinter einer Zimmertür: was dort typisch ist zuerst. */
const NEU: RoomType[] = ["balkon", "abstellraum", "bad", "schlafen", "kind", "arbeit", "kueche", "wohnen"];
const S = 220;
const H = S / 2;
const WAENDE: Wand[] = [
  { id: "links", a: { x: -H, y: H }, b: { x: -H, y: -H }, out: { x: -1, y: 0 } },
  { id: "gegenueber", a: { x: -H, y: -H }, b: { x: H, y: -H }, out: { x: 0, y: -1 } },
  { id: "rechts", a: { x: H, y: H }, b: { x: H, y: -H }, out: { x: 1, y: 0 } },
  { id: "tuer", a: { x: -H, y: H }, b: { x: H, y: H }, out: { x: 0, y: 1 } },
];

export function RaumTueren(props: { room: Room }) {
  const d = useDraft();
  const store = useStore();
  const room = d.rooms.find((r) => r.id === props.room.id) ?? props.room;
  const [neu, setNeu] = useState<{ wall: RoomWall; pos: number } | null>(null);
  const [auswahl, setAuswahl] = useState<string | null>(null);

  const doors = (room.doors ?? []).filter((x) => x.status === "raum");
  const save = (list: RoomDoor[]) => {
    const keep = (store.getSnapshot().rooms.find((r) => r.id === room.id)?.doors ?? []).filter((x) => x.status !== "raum");
    store.saveRoom({ ...room, doors: [...keep, ...list] });
  };
  const target = (x: RoomDoor) => (x.to ? d.rooms.find((r) => r.id === x.to) : null);
  const others = d.rooms.filter((r) => r.id !== room.id && r.type !== "flur");
  const nr = (i: number) => String(i + 1);

  const tueren: SkizzenTuer[] = [
    { key: "eingang", wall: "tuer", pos: 0.5, nr: "E", label: "", fest: true },
    ...doors.map((x, i) => ({ key: x.id, wall: x.wall ?? "gegenueber", pos: x.x ?? 0.5, nr: nr(i), label: target(x)?.name ?? "?", aktiv: auswahl === x.id })),
    ...(neu ? [{ key: "neu", wall: neu.wall, pos: neu.pos, nr: "+", label: "?", aktiv: true }] : []),
  ];

  /** Eben erst angelegten, noch leeren Raum hinter einer Tür wieder entfernen, wenn sich die Antwort ändert. */
  const dropCreated = (x: RoomDoor | null) => {
    const r = x?.to ? d.rooms.find((y) => y.id === x.to) : null;
    if (r && r.hallRoomId === room.id && !d.photos.some((p) => p.roomId === r.id)) store.deleteRoom(r.id);
  };
  const decide = (door: RoomDoor | null, wall: RoomWall, pos: number, v: { to?: string; type?: RoomType }) => {
    dropCreated(door);
    let to = v.to ?? null;
    if (v.type) {
      to = newId();
      store.saveRoom({
        id: to,
        type: v.type,
        name: roomName(v.type, 1),
        sort: Math.min(1000, room.sort + 1),
        confirmedComplete: false,
        floor: room.floor ?? 0,
        hallRoomId: room.id,
        doorWall: wall,
        photoMode: defaultPhotoMode(v.type),
      });
    }
    const kind: RoomDoor["kind"] = v.type === "balkon" || target({ to } as RoomDoor)?.type === "balkon" ? "fenstertuer" : "tuer";
    const next: RoomDoor = { ...(door ?? { id: newId(), photoId: null, y: null, source: "kunde" as const, status: "raum" as const }), x: pos, wall, kind, status: "raum", to };
    save([...doors.filter((x) => x.id !== next.id), next]);
    tidyNames(store);
    setNeu(null);
    setAuswahl(null);
  };
  const removeDoor = (x: RoomDoor) => {
    dropCreated(x);
    save(doors.filter((y) => y.id !== x.id));
    tidyNames(store);
    setAuswahl(null);
  };
  const onDrop = (key: string | null, wall: string, pos: number) => {
    if (key === "eingang") return;
    if (key === null || key === "neu") {
      setNeu({ wall: wall as RoomWall, pos });
      setAuswahl("neu");
      return;
    }
    save(doors.map((x) => (x.id === key ? { ...x, wall: wall as RoomWall, x: pos } : x)));
    setAuswahl(key);
  };

  const selected = auswahl && auswahl !== "neu" ? doors.find((x) => x.id === auswahl) : null;
  const [offen, setOffen] = useState(doors.length > 0);

  // Seit 30.10.2026 kein eigener Bildschirm mehr: zugeklappt unter den Wandfotos, die meisten Räume haben keine weitere Tür.
  if (!offen) {
    return (
      <button type="button" className="link tuer-plus" onClick={() => setOffen(true)}>
        + Tür in einen weiteren Raum (Balkon, Abstellraum …)
      </button>
    );
  }

  return (
    <>
      <section className="card">
        <h2>Weitere Tür in {room.name === ROOM_TYPE_LABEL[room.type] ? "diesem Raum" : room.name}</h2>
        <p className="tip">
          <strong>Finger auf eine Wand legen, kurz halten, an die richtige Stelle ziehen und loslassen.</strong> Unten ist die Tür, durch die Sie
          hereingekommen sind (E).
        </p>
        <div className="zieh-rahmen raum">
          <ZiehSkizze walls={WAENDE} doors={tueren} box={{ x: -H - 90, y: -H - 40, w: S + 180, h: S + 80 }} label={`Skizze: ${room.name} von oben`} onDrop={onDrop} onTap={(k) => k !== "eingang" && setAuswahl(auswahl === k ? null : k)}>
            <rect x={-H} y={-H} width={S} height={S} className="skizze-raum" />
            <text x="0" y="4" textAnchor="middle" className="skizze-raumname">
              {room.name}
            </text>
          </ZiehSkizze>
        </div>
        {(neu || selected) && (
          <div className="neue-tuer card">
            <h3>{neu ? "Wohin führt die neue Tür?" : `Tür ${nr(doors.indexOf(selected!))}: ${target(selected!)?.name ?? "?"}`}</h3>
            {others.length > 0 && (
              <>
                <span className="label">Zu einem schon erfassten Raum</span>
                <div className="chips">
                  {others.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className={`chip${selected?.to === r.id ? " on" : ""}`}
                      onClick={() => (neu ? decide(null, neu.wall, neu.pos, { to: r.id }) : decide(selected!, selected!.wall ?? "gegenueber", selected!.x ?? 0.5, { to: r.id }))}
                    >
                      {r.name}
                    </button>
                  ))}
                </div>
              </>
            )}
            <span className="label">Zu einem neuen Raum</span>
            <RaumChips
              current={null}
              typen={NEU}
              onPick={(t) => (neu ? decide(null, neu.wall, neu.pos, { type: t }) : decide(selected!, selected!.wall ?? "gegenueber", selected!.x ?? 0.5, { type: t }))}
            />
            <div className="aktionen-zeile">
              {selected && (
                <Button variant="quiet" onClick={() => removeDoor(selected)}>
                  Tür löschen
                </Button>
              )}
              <Button
                variant="quiet"
                onClick={() => {
                  setNeu(null);
                  setAuswahl(null);
                }}
              >
                {neu ? "Abbrechen" : "Fertig"}
              </Button>
            </div>
          </div>
        )}
        {doors.length === 0 && !neu && (
          <button type="button" className="link" onClick={() => setOffen(false)}>
            Doch keine weitere Tür
          </button>
        )}
      </section>
    </>
  );
}
